import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    FormInputComponent,
    InputComponentConfig,
    DataService,
    NotificationService,
} from '@vendure/admin-ui/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Promotion } from '@vendure/core';
import { BehaviorSubject, filter } from 'rxjs';
import { getPromotionQuery } from './helpers/gql';
import { MinVariantAllowed, VariantWithMinQuantity } from './helpers/types';

@Component({
    standalone: true,
    selector: 'product-variant-flexible-quantity-form-input',
    imports: [CommonModule, ReactiveFormsModule, FormsModule],
    template: `
        <div class="container" *ngIf="variant; else selectTemplate">
            <div>
                <div *ngIf="variant.featuredAsset">
                    <img
                        [src]="variant.featuredAsset.source"
                        alt="Product Variant"
                        width="20"
                    />
                </div>
                <div class="informations">
                    <span>ID: {{ variant.id }}</span>
                    <span>SKU: {{ variant.sku }}</span>
                </div>
            </div>
            <span>Mínima quantidade permitida: {{ variant.minQuantity }}</span>
        </div>
        <ng-template #selectTemplate>
            <div *ngIf="availableVariantsIds.length > 0">
                <label for="variant-select">Selecione a Variante:</label> <br />
                <select id="variant-select" (change)="onVariantSelect($event)">
                    <option value="">-- Selecione --</option>
                    <option
                        *ngFor="let variant of variants"
                        [value]="variant.id"
                    >
                        {{ variant.sku }} - {{ variant.id }}
                    </option>
                </select>
            </div>
        </ng-template>
    `,
    styles: [
        `
            img {
                width: 20%;
            }

            .informations {
                display: flex;
                flex-direction: column;
                justify-content: center;
            }

            .container {
                margin-bottom: 30px;
            }

            .custom-button {
                margin-left: 20px;
            }
        `,
    ],
})
export class ProductVariantFlexibleQuantityFormInput
    implements FormInputComponent<InputComponentConfig>, OnInit
{
    @Input() readonly: boolean = false;
    @Input() formControl!: FormControl;
    @Input() config!: InputComponentConfig;

    variants: VariantWithMinQuantity[] = [];
    variant: VariantWithMinQuantity | null = null;

    private _availableVariantsIds: MinVariantAllowed[] = [];
    private availableVariantsIds$ = new BehaviorSubject<MinVariantAllowed[]>(
        []
    );

    constructor(
        private dataService: DataService,
        private notificationService: NotificationService,
        private cd: ChangeDetectorRef,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        if (!this.formControl.value) {
            this.formControl.setValue('');
        } else {
            this.searchVariantById(this.formControl.value?.toString());
        }

        // Handling with available variants
        this.getAvailableVariantsIds();
        this.availableVariantsIds$
            .pipe(filter((variants) => variants && variants.length > 0))
            .subscribe((variants) => {
                this.searchVariantByIds(variants);
            });
    }

    searchVariantById(id: string): void {
        this.dataService.product
            .getProductVariants({
                filter: { id: { eq: id } },
            })
            .mapStream((response) => response.productVariants.items)
            .subscribe({
                next: (variants) => {
                    const quantity = this.availableVariantsIds.find(
                        (v) => v.variantId === variants[0].id
                    );

                    this.variant = {
                        ...variants[0],
                        minQuantity: quantity?.minQuantity || 0,
                    };
                    this.cd.detectChanges();
                },
                error: (err) => {
                    this.notificationService.error(err);
                },
            });
    }

    searchVariantByIds(payload: MinVariantAllowed[]): void {
        const variantIds = payload.map((item) => item.variantId);

        this.dataService.product
            .getProductVariants({
                filter: { id: { in: variantIds } },
            })
            .mapStream((response) => response.productVariants.items)
            .subscribe({
                next: (variants) => {
                    this.variants = variants.map((variant) => {
                        const quantity = payload.find(
                            (v) => v.variantId === variant.id
                        );

                        return {
                            ...variant,
                            minQuantity: quantity?.minQuantity || 0,
                        };
                    });

                    this.cd.detectChanges();
                },
                error: (err) => {
                    this.notificationService.error(
                        'Erro ao buscar variantes: ',
                        err
                    );
                },
            });
    }

    onVariantSelect(event: Event): void {
        const select = event.target as HTMLSelectElement;
        const selectedId = select.value;

        // Atualiza o FormControl com o ID da variante selecionada
        if (selectedId) {
            this.variant =
                this.variants.find((v) => v.id === selectedId) || null;
            this.formControl.setValue(selectedId);
        }
    }

    getAvailableVariantsIds() {
        const promotionId = this.route.snapshot.paramMap.get('id');
        if (!promotionId) return;

        const query = getPromotionQuery(promotionId);

        this.dataService
            .query<{ promotion: Promotion }>(query)
            .mapStream((item) => item)
            .subscribe({
                next: (response) => {
                    const condition = response.promotion.conditions.find(
                        (condition) => {
                            return condition.code === 'flexible_variants';
                        }
                    );

                    const arg = condition?.args.find(
                        (arg) => arg.name === 'variants_options'
                    );
                    if (!arg?.value) return;

                    this.availableVariantsIds = JSON.parse(
                        arg.value
                    ) as MinVariantAllowed[];
                },
                error: (error) => {
                    this.notificationService.error(error);
                },
            });
    }

    set availableVariantsIds(ids: MinVariantAllowed[]) {
        this._availableVariantsIds = ids;
        this.availableVariantsIds$.next(ids); // Atualiza o BehaviorSubject
    }

    get availableVariantsIds(): MinVariantAllowed[] {
        return this._availableVariantsIds;
    }
}
